import React, {FC, useState} from "react";
import {Alert, Checkbox, Input} from "antd";
import {TransferPlanPreviewQuery, TransferItemDecisionInput, ScheduledPaymentType, TransferOldBookingTreatment} from "../../generated/graphql";
import {renderAmount, CurrencyShort} from "../../helpers/renderAmount";
import i18n from "../../services/i18n";
import formatDate from "../../helpers/formatDate";

type Plan = Pick<TransferPlanPreviewQuery["transferPlanPreview"], "priceB" | "discountB" | "discountReason" | "paidA" | "appliedToB" | "surplus" | "creditAfter" | "rates" | "carriedOverItems" | "itemReview" | "oldBooking" | "hints"> & {
    /** Verwendung des Überschusses (#665); fehlt sie (ältere Aufrufer), bleibt der ganze Überschuss Guthaben. */
    surplusHandling?: Pick<TransferPlanPreviewQuery["transferPlanPreview"]["surplusHandling"], "creditAmount"> | null;
};

const paidItemExplanation = (type: ScheduledPaymentType): string => {
    const tr = i18n.containers.bookings.TransferEngineConfirm;

    return type === ScheduledPaymentType.Custom || type === ScheduledPaymentType.AdditionalItem ? tr.paidItemInfo() : tr.paidFeeInfo();
};

/**
 * `value`: bereits getroffene Entscheidungen (Wizard #662, Zurück-Navigation). Ohne `value`
 * werden offene Posten übernommen. Die Anzeige muss dem Elternzustand entsprechen — sonst
 * würde ein versteckter Verzicht ausgeführt, den die Häkchen nicht zeigen.
 */
export const TransferItemChoices: FC<{items: Plan["itemReview"]; currency?: CurrencyShort; canWaive: boolean; value?: Array<TransferItemDecisionInput>; onChange: (value: Array<TransferItemDecisionInput>) => void}> = ({items, currency, canWaive, value, onChange}) => {
    const tr = i18n.containers.bookings.TransferEngineConfirm;
    const [choices, setChoices] = useState<Array<TransferItemDecisionInput>>(() => items.filter((item) => item.open > 0).map((item) => value?.find((decision) => decision.sourceRateId === item.sourceRateId) ?? {sourceRateId: item.sourceRateId, carry: true}));
    const change = (id: string, patch: Partial<TransferItemDecisionInput>) => {
        const next = choices.map((choice) => choice.sourceRateId === id ? {...choice, ...patch} : choice);

        setChoices(next);
        onChange(next);
    };

    // Ein Haken je Posten: übernehmen oder nicht. Kein Pflicht-Grund, keine Warnbox — die Wahl
    // steht mit Betrag in Vorschau und Historie (Abnahme 23.09.2026).
    return <div style={{maxHeight: "60vh", overflowY: "auto"}}>
        <p style={{color: "rgba(0,0,0,0.65)"}}>{tr.itemsIntro()}</p>
        {items.map((item) => {
            const choice = choices.find((c) => c.sourceRateId === item.sourceRateId);

            return <div key={item.sourceRateId} style={{padding: "6px 0"}}>
                {choice ? <>
                    <Checkbox checked={choice.carry} disabled={!canWaive} onChange={(e) => change(item.sourceRateId, {carry: e.target.checked})}>
                        <b>{item.title}</b>{" · "}{tr.carryItemAmount({amount: renderAmount({amount: item.open, currency})})}
                    </Checkbox>
                    {item.paid > 0 && <div style={{marginLeft: 24, color: "rgba(0,0,0,0.45)", fontSize: 12}}>{tr.itemPaid()}{": "}{renderAmount({amount: item.paid, currency})}</div>}
                    {!choice.carry && <div style={{marginLeft: 24, color: "rgba(0,0,0,0.45)", fontSize: 12}}>{tr.waiveExplanation()}</div>}
                </> : <div><b>{item.title}</b>{" · "}{tr.itemPaid()}{": "}{renderAmount({amount: item.paid, currency})}<div style={{color: "rgba(0,0,0,0.45)", fontSize: 12}}>{paidItemExplanation(item.type)}</div></div>}
            </div>;
        })}
        {!canWaive && <p style={{color: "rgba(0,0,0,0.45)", fontSize: 12}}>{tr.waiveAdminOnly()}</p>}
    </div>;
};

/** `mailHint`: der Legacy-Satz „keine automatische Umbuchungs-E-Mail" — der Wizard (#664) zeigt stattdessen die gewählte Kommunikation. */
const TransferPlanDetails: FC<{plan: Plan; currency?: CurrencyShort; waitlist?: boolean; completed?: boolean; mailHint?: boolean}> = ({plan, currency, waitlist, completed, mailHint = true}) => {
    const tr = i18n.containers.bookings.TransferEngineConfirm;
    const money = (amount: number) => renderAmount({amount: Math.round(amount * 100) / 100, currency});
    const total = plan.priceB + plan.carriedOverItems.reduce((sum, item) => sum + item.amount, 0);
    const surplusCredit = plan.surplusHandling ? plan.surplusHandling.creditAmount : plan.surplus;
    const completedTreatment = plan.oldBooking.treatment === TransferOldBookingTreatment.Delete ? tr.oldDeleted() : plan.oldBooking.treatment === TransferOldBookingTreatment.CancelWithReceipt ? tr.oldCanceledWithReceipt() : tr.oldCanceledWithoutReceipt();

    return <div style={{maxHeight: "60vh", overflowY: "auto"}}>
        <p><b>{tr.rows.oldBooking()}</b><br />{completed ? completedTreatment : plan.oldBooking.reason}</p>
        <div>{tr.rows.priceB()}{": "}<b>{money(plan.priceB)}</b></div>
        {plan.discountB > 0 && <div>{tr.rows.discountB()}{": "}{money(plan.discountB)}{plan.discountReason ? ` — ${plan.discountReason}` : ""}</div>}
        {(plan.itemReview ?? []).length === 0 && plan.carriedOverItems.map((item, index) => <div key={index}>{item.title ?? tr.carriedOverItem()}{": "}{money(item.amount)}</div>)}
        {(plan.itemReview ?? []).map((item) => <div key={item.sourceRateId} style={{marginTop: 8}}>
            <b>{item.title}</b>{": "}{tr.itemCarried()} {money(item.carried)}
            {item.paid > 0 && <div>{tr.itemPaid()}{": "}{money(item.paid)}{" — "}{paidItemExplanation(item.type)}</div>}
            {item.waived > 0 && <div>{tr.itemWaived()}{": "}{money(item.waived)}{" · "}{tr.waiveReason()}{": "}{item.reason}</div>}
        </div>)}
        <div>{tr.rows.total()}{": "}<b>{money(total)}</b></div>
        <div>{tr.transferablePayments()}{": "}{money(plan.paidA)}</div>
        <div>{tr.rows.appliedToB()}{": "}{money(plan.appliedToB)}</div>
        <p><b>{waitlist ? tr.dueOnPromotion() : tr.amountDue()}{": "}{money(total - plan.appliedToB)}</b></p>
        <div>{tr.rows.creditAfter()}{": "}{money(plan.creditAfter)}</div>
        {plan.creditAfter > 0 && <div>{tr.creditNotApplied()}</div>}
        {/* Nur der Teil, der Guthaben bleibt — bei Mollie- oder manueller Erstattung (#665) nicht der ganze Überschuss. */}
        {surplusCredit > 0 && <div>{tr.rows.surplus()}{": "}{money(surplusCredit)}</div>}
        <p style={{marginBottom: 4}}><b>{tr.paymentSchedule()}</b></p>
        <ul>{plan.rates.map((rate, index) => <li key={index}>
            {rate.title ?? tr.courseRate()}{": "}{money(rate.amount)}{" · "}{tr.dueDate()} {formatDate(new Date(String(rate.due)), "dd.MM.yyyy")}{" · "}{tr.itemOpen()} {money(rate.amount - rate.settledByTransfer)}
        </li>)}</ul>
        {waitlist && <Alert type="info" message={tr.waitlist()} />}
        {mailHint && <p>{tr.noAutomaticMail()}</p>}
        {plan.hints.length > 0 && <ul>{plan.hints.map((hint, index) => <li key={index}>{hint}</li>)}</ul>}
    </div>;
};

export default TransferPlanDetails;
